import LayoutUtil from '@frontend/utils/LayoutUtil';
import focusLock from 'dom-focus-lock';
import Header from '@modules/001-Header/Header.client';
import BaseSlider from '@components/base/BaseSlider';
import { AnchorNavigationEvents } from '@frontend/utils/AnchorNavigationUtil';

export default class Modal {
	public static domSelectors = {
		openTrigger: '.c-modal__trigger',
		modalPane: '.c-modal__pane',
		modalOuter: '.c-modal__outer',
		backdrop: '.c-modal__backdrop',
		closeButton: '.c-modal__close-button'
	};

	private static stateClasses = {
		isVisible: 'is-visible',
		isDisplayed: 'is-displayed',
		hasOpenModal: 'has-open-modal'
	};

	public static events = {
		MODAL_OPENED: 'Modal.opened',
		MODAL_CLOSED: 'Modal.closed'
	};

	private readonly element: HTMLElement;

	private launchElement?: Element;

	private readonly modalPane: HTMLElement;

	private onKeyDownWhileOpenListener?: OmitThisParameter<(event: KeyboardEvent) => void>;

	public constructor(element: HTMLElement) {
		this.element = element;
		this.modalPane = this.element.querySelector(Modal.domSelectors.modalPane) as HTMLElement;
		this.init();
	}

	private init(): void {
		this.closeModal = this.closeModal.bind(this);
		this.closeModalFromOuterClick = this.closeModalFromOuterClick.bind(this);

		const trigger = this.element.querySelector(Modal.domSelectors.openTrigger);

		if (trigger) {
			trigger.addEventListener('click', (): void => {
				this.openModal();
			});
		}

		this.moveMarkup();

		document.addEventListener(AnchorNavigationEvents.change, this.anchorLinkHandler.bind(this));
	}

	public openModal(): void {
		const closeButton: HTMLElement | null = this.modalPane.querySelector(Modal.domSelectors.closeButton) as HTMLElement;
		const backdrop: HTMLElement | null = this.modalPane.querySelector(Modal.domSelectors.backdrop) as HTMLElement;
		const outer: HTMLElement | null = this.modalPane.querySelector(Modal.domSelectors.modalOuter) as HTMLElement;

		const launchElement = document.activeElement;
		if (launchElement) {
			this.launchElement = launchElement;
		}

		closeButton.addEventListener('click', this.closeModal);
		backdrop.addEventListener('click', this.closeModal);
		outer.addEventListener('click', this.closeModalFromOuterClick);

		this.onKeyDownWhileOpenListener = this.onKeyDownWhileOpen.bind(this);
		document.addEventListener('keydown', this.onKeyDownWhileOpenListener);

		focusLock.on(this.modalPane);
		LayoutUtil.deactivateMainContainer();
		this.element.dispatchEvent(new CustomEvent(Modal.events.MODAL_OPENED));

		this.modalPane.classList.add(Modal.stateClasses.isDisplayed);

		setTimeout((): void => {
			this.modalPane.classList.add(Modal.stateClasses.isVisible);
			closeButton.focus();
			BaseSlider.resizeSliders(this.getModalPane());
		}, 10);
	}

	public closeModal(): void {
		const closeButton: HTMLElement | null = this.modalPane.querySelector(Modal.domSelectors.closeButton) as HTMLElement;
		const backdrop: HTMLElement | null = this.modalPane.querySelector(Modal.domSelectors.backdrop) as HTMLElement;
		const outer: HTMLElement | null = this.modalPane.querySelector(Modal.domSelectors.modalOuter) as HTMLElement;

		closeButton.removeEventListener('click', this.closeModal);
		backdrop.removeEventListener('click', this.closeModal);
		outer.removeEventListener('click', this.closeModalFromOuterClick);

		if (this.onKeyDownWhileOpenListener) {
			document.removeEventListener('keydown', this.onKeyDownWhileOpenListener);
			delete this.onKeyDownWhileOpenListener;
		}

		this.modalPane.classList.remove(Modal.stateClasses.isVisible);

		setTimeout((): void => {
			this.modalPane.classList.remove(Modal.stateClasses.isDisplayed);
			focusLock.off(this.modalPane);

			if (!Header.isOverlayOpen()) {
				LayoutUtil.activateMainContainer();
			}

			this.element.dispatchEvent(new CustomEvent(Modal.events.MODAL_CLOSED));

			if (this.launchElement) {
				(this.launchElement as HTMLElement).focus();
			}
		}, 200);
	}

	// workaround for the blue-bubble modal, as it is covering the backdrop which normally closes the modal
	public closeModalFromOuterClick(event: MouseEvent): void {
		if (event.target === this.modalPane.querySelector(Modal.domSelectors.modalOuter)) {
			this.closeModal();
		}
	}

	public getModalPane(): HTMLElement {
		return this.modalPane;
	}

	protected anchorLinkHandler(event: CustomEventInit): void {
		const modalPane = this.getModalPane();
		const isTargetModal = this.element.id === event.detail.target.replace('#', '');
		const isTargetInsideModal = modalPane.querySelector(event.detail.target);

		if (isTargetModal || isTargetInsideModal) {
			this.openModal();
		}
	}

	private moveMarkup(): void {
		const mainLayoutWrapper = document.querySelector('.l-main');
		const pane = this.element.querySelector(Modal.domSelectors.modalPane);

		if (!mainLayoutWrapper || !pane) return;

		mainLayoutWrapper.after(pane);
	}

	private onKeyDownWhileOpen(event: KeyboardEvent): void {
		if (event.key === 'Escape') {
			this.closeModal();
		}
	}
}
